/**
 * Handing the customer app over at the reception (no SMS provider needed):
 *  - the app link that opens the customer sign-in on "Setup code" with the mobile and the code already filled in,
 *  - the WhatsApp message that carries it (a wa.me link the reception taps; WhatsApp sends it from their phone),
 *  - and what the sign-in page reads back from such a link.
 *
 * The code rides in the address's #fragment, which a browser never sends to any server (no access log or proxy
 * sees it); the mobile number is in the query. The code works once, for a short time, and only with that mobile.
 */

/** A new PIN has 4 digits (the owner's choice); PINs chosen earlier with 5 or 6 digits still sign in. */
export const NEW_PIN_DIGITS = 4;
export const newPinProblem = (pin: string): string | null => (new RegExp(`^\\d{${NEW_PIN_DIGITS}}$`).test(pin) ? null : `Choose a PIN of exactly ${NEW_PIN_DIGITS} digits.`);

/** The digits of a 10-digit Indian mobile (+91 dropped), or "+<digits>" for another country; null when there are too few digits. */
export function setupMobileParam(mobile: string | null | undefined): string | null {
  if (!mobile) return null;
  const digits = mobile.replace(/\D/g, '');
  if (mobile.trim().startsWith('+') && !digits.startsWith('91')) return digits.length >= 8 ? `+${digits}` : null;
  const local = digits.length === 12 && digits.startsWith('91') ? digits.slice(2) : digits.length === 11 && digits.startsWith('0') ? digits.slice(1) : digits;
  return /^\d{10}$/.test(local) ? local : null;
}

/**
 * /app/login?setup=<mobile>#code=<code> on this site. A printed slip passes null for the mobile (/app/login?setup=#code=…):
 * a receipt left on the counter or photographed must not carry the full number, so the customer types it.
 */
export function appSetupLink(origin: string, mobile: string | null, code: string): string {
  const m = (mobile && setupMobileParam(mobile)) || '';
  return `${origin.replace(/\/+$/, '')}/app/login?setup=${encodeURIComponent(m)}#code=${encodeURIComponent(code)}`;
}

/** What the customer sign-in reads from the address it was opened with (null when it is not a setup link). */
export function readSetupLink(search: string, hash: string): { mobile: string; code: string } | null {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (!q.has('setup')) return null;
  const h = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const mobile = (q.get('setup') ?? '').replace(/[^\d+]/g, '').slice(0, 16);
  const code = (h.get('code') ?? '').replace(/\D/g, '').slice(0, 6);
  return { mobile, code };
}

export interface SetupMessage {
  business: string;
  customerName?: string | null;
  code: string;
  link: string;
  minutes: number;
}

/** The message the customer gets on WhatsApp, in plain words. */
export function setupMessage(m: SetupMessage): string {
  const first = m.customerName?.trim().split(/\s+/)[0];
  return [
    `${first ? `Hello ${first}, welcome` : 'Welcome'} to ${m.business}!`,
    `Your ${m.business} app setup code: ${m.code}`,
    `1. Open ${m.link}`,
    '2. Check your mobile number and the code, then choose a 4-digit PIN.',
    `The code works once, for ${m.minutes} minutes. Please do not share it.`,
  ].join('\n');
}

/** A wa.me link that opens WhatsApp with the message, addressed to the customer's number (null without a usable number). */
export function whatsAppLink(mobile: string | null | undefined, text: string): string | null {
  if (!mobile) return null;
  const digits = mobile.replace(/\D/g, '');
  // A 10-digit number typed without the country code is Indian.
  const full = digits.length === 10 ? `91${digits}` : digits.length === 11 && digits.startsWith('0') ? `91${digits.slice(1)}` : digits;
  if (full.length < 8 || full.length > 15) return null;
  return `https://wa.me/${full}?text=${encodeURIComponent(text)}`;
}

/** "3:45 pm": when a code issued now (or at `from`) stops working. */
export function validUntil(minutes: number, from: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit' }).format(new Date(from.getTime() + minutes * 60_000));
}

/** The customer app in production, for links sent or printed from a page that is not served over the web (the desktop app shell). */
export const APP_ORIGIN_FALLBACK = 'https://carnival-city.pages.dev';

/**
 * Where the customer app lives: this site when it is served over http(s) (https://carnival-city.pages.dev in
 * production, the UAT site on UAT), else the production site. `current` is this page's origin (window.location.origin).
 */
export function appOrigin(current?: string): string {
  const o = current ?? (typeof window !== 'undefined' ? window.location.origin : '');
  return /^https?:\/\//.test(o) ? o : APP_ORIGIN_FALLBACK;
}
